import React, { useState } from 'react';
import './AdminDocumentation.css';

function AdminDocumentation() {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: 'Overview', icon: 'fa-eye' },
    { id: 'architecture', title: 'Architecture', icon: 'fa-sitemap' },
    { id: 'idsystem', title: 'ID System Guide', icon: 'fa-id-card' },
    { id: 'roles', title: 'Admin Roles', icon: 'fa-shield-halved' },
    { id: 'user-management', title: 'User Management', icon: 'fa-users-gear' },
    { id: 'changelog-mgmt', title: 'Changelog/Versions', icon: 'fa-history' },
    { id: 'broadcasts', title: 'Broadcast Admin', icon: 'fa-bullhorn' },
    { id: 'security-audit', title: 'Security Audit', icon: 'fa-microscope' },
    { id: 'target-identity', title: 'Target Identity', icon: 'fa-magnifying-glass-chart' },
    { id: 'monitoring', title: 'Stats & Monitoring', icon: 'fa-terminal' },
    { id: 'resolutions', title: 'Issue Resolutions', icon: 'fa-check-to-slot' },
    { id: 'database', title: 'Database Structure', icon: 'fa-database' },
    { id: 'security-guidelines', title: 'Security Guidelines', icon: 'fa-hand-shield' },
    { id: 'ai-intel', title: 'Admin AI Intel', icon: 'fa-brain' },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="doc-section animate-up">
            <h2>Section 1 — Overview</h2>
            <p>DevTracker is designed to be the central nervous system for developer identity. It allows developers to consolidate their coding presence into a single, professional dashboard.</p>
            <div className="platform-grid">
              <div className="platform-item"><i className="fa-brands fa-github"></i> GitHub</div>
              <div className="platform-item"><i className="fa-solid fa-code"></i> LeetCode</div>
              <div className="platform-item"><i className="fa-solid fa-chart-simple"></i> Codeforces</div>
              <div className="platform-item"><i className="fa-solid fa-laptop-code"></i> GeeksforGeeks</div>
            </div>
            <p>As an administrator, your role is to ensure the platform remains secure, the data remains accurate, and the users are supported through their integration journey.</p>
          </div>
        );
      case 'architecture':
        return (
          <div className="doc-section animate-up">
            <h2>Section 2 — System Architecture</h2>
            <div className="architecture-block">
              <h3>Frontend Stack</h3>
              <ul>
                <li><strong>React JS:</strong> Core UI framework.</li>
                <li><strong>Individual Dashboards:</strong> Where users manage their links.</li>
                <li><strong>Public Profiles:</strong> The shareable, public-facing developer resumes.</li>
                <li><strong>Embed Widgets:</strong> Specialized light-weight components for third-party site integration.</li>
              </ul>
            </div>
            <div className="architecture-block">
              <h3>Backend Infrastructure</h3>
              <ul>
                <li><strong>Firebase Auth:</strong> Secure identity management.</li>
                <li><strong>Realtime Database:</strong> High-speed data synchronization.</li>
                <li><strong>Admin Panel:</strong> Internal command center for staff.</li>
              </ul>
            </div>
          </div>
        );
      case 'idsystem':
        return (
          <div className="doc-section animate-up">
            <h2>System Identifiers</h2>
            <p>DevTracker uses a standardized <strong>Prefix-DVT-XXXXXX</strong> architecture for all identifiers to ensure anonymity and auditability.</p>
            <div className="id-card-list">
              <div className="id-card">
                <code>UID-DVT-XXXXXX</code>
                <p><strong>UID:</strong> User Identifier. Primary reference for account credentials.</p>
              </div>
              <div className="id-card">
                <code>PID-DVT-XXXXXX</code>
                <p><strong>PID:</strong> Profile ID. Used in public URLs (e.g., <code>/dev/PID-DVT-123</code>).</p>
              </div>
              <div className="id-card">
                <code>SUID-DVT-XXXXXX</code>
                <p><strong>SUID:</strong> Support Identifier. Used for privacy in help tickets.</p>
              </div>
              <div className="id-card">
                <code>NID-DVT-XXXXXX</code>
                <p><strong>NID:</strong> Notification ID. Unique index for platform broadcasts.</p>
              </div>
              <div className="id-card">
                <code>REP-DVT-XXXXXX</code>
                <p><strong>REP:</strong> Report ID. Tracker for bugs, appeals, and complaints.</p>
              </div>
              <div className="id-card">
                <code>AID-DVT-XXXXXX</code>
                <p><strong>AID:</strong> Admin Identifier. Your unique identity for system logs.</p>
              </div>
            </div>
          </div>
        );
      case 'roles':
        return (
          <div className="doc-section animate-up">
            <h2>Section 4 — Admin Roles</h2>
            <div className="role-table-wrapper">
              <table className="doc-table">
                <thead>
                  <tr>
                    <th>Role</th>
                    <th>Permissions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="badge badge-super">Super Admin</span></td>
                    <td>Full platform access, admin management, global settings, audit log review.</td>
                  </tr>
                  <tr>
                    <td><span className="badge badge-mod">Moderator</span></td>
                    <td>User suspension, profile moderation, data correction.</td>
                  </tr>
                  <tr>
                    <td><span className="badge badge-support">Support</span></td>
                    <td>Ticketing system access, SUID lookup, resolving contact messages.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'user-management':
        return (
          <div className="doc-section animate-up">
            <h2>Section 5 — User Management</h2>
            <h3>Available Toolset</h3>
            <ul>
              <li><strong>View Users:</strong> Monitor sign-up activity and profile health.</li>
              <li><strong>Suspension:</strong> Temporarily block access for TOS violations.</li>
              <li><strong>Deletion:</strong> Hard-delete accounts upon user request or fraudulent activity.</li>
            </ul>
            <div className="callout note">
              <i className="fa-solid fa-magnifying-glass"></i>
              <span>Admins can search the database globally using <strong>UID, Email,</strong> or <strong>Profile ID</strong>.</span>
            </div>
          </div>
        );
      case 'changelog-mgmt':
        return (
          <div className="doc-section animate-up">
            <h2>Changelog & Version Management</h2>
            <p>Platform updates are no longer hardcoded. Use the <strong>Release Manager</strong> to publish system updates.</p>
            <div className="callout info">
              <i className="fa-solid fa-code-merge"></i>
              <span>Updates recorded here sync instantly to the public <code>/changelog</code> route and all platform footers.</span>
            </div>
            <ul>
              <li><strong>Version String:</strong> Use SemVer (e.g., <code>v1.5.0</code>).</li>
              <li><strong>Description:</strong> Brief overview for the header.</li>
              <li><strong>Feature Points:</strong> Bulleted list of specific technical changes.</li>
            </ul>
          </div>
        );
      case 'broadcasts':
        return (
          <div className="doc-section animate-up">
            <h2>Broadcast Communications</h2>
            <p>The <strong>Notification Center</strong> allows administrators to send real-time alerts to the userbase. Since <strong>v2.6.7</strong>, all broadcasts include explicit attribution.</p>
            <div className="role-table-wrapper">
              <table className="doc-table">
                <thead>
                  <tr>
                    <th>Attribution</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td><code>Human</code></td><td>Standard broadcast sent directly by an administrator.</td></tr>
                  <tr><td><code>AI Assisted</code></td><td>Content drafted using the AI generator (marked with a robot badge).</td></tr>
                  <tr><td><code>System</code></td><td>Automated alerts triggered by platform logic.</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      case 'security-audit':
        return (
          <div className="doc-section animate-up">
            <h2>Neural Security Scanner (NSS-V1.0.9)</h2>
            <p>The <strong>NSS</strong> is our high-fidelity diagnostic engine for real-time integrity validation across the Origin Core network.</p>
            <div className="callout warning">
              <i className="fa-solid fa-microchip-ai"></i>
              <span>The Zenith Sentinel Matrix (v2.6.9.6) provides the ultimate holographic monitoring layer for Origin Core.</span>
            </div>
            <ul>
              <li><strong>Holographic Threat Map:</strong> Global visualization of security nodes and real-time anomaly pings.</li>
              <li><strong>Neural Heartbeat:</strong> Reactive waveform diagnostics representing platform-wide synchronicity.</li>
              <li><strong>Ultra-Fidelity Optics:</strong> Zenith-grade CRT phosphor effects and intensified holographic depth.</li>
              <li><strong>SEC-V2.6.9.6-ULTRA:</strong> Operation at the platform's zenith security baseline for all governance vectors.</li>
            </ul>
          </div>
        );
      case 'target-identity':
        return (
          <div className="doc-section animate-up">
            <h2>Target Identity Intelligence</h2>
            <p>The <strong>Target Identity</strong> portal (v2.6.8) is the central lookup engine for investigating any platform identity.</p>
            <div className="workflow">
              <div className="step"><span>1</span> <strong>Scan:</strong> Input an SUID, UID, Username, or Email to begin.</div>
              <div className="step"><span>2</span> <strong>Analyze:</strong> Review the 360-degree dossier (Technical, Governance, Social).</div>
              <div className="step"><span>3</span> <strong>Audit:</strong> View linked Reports and Cases associated with the identity.</div>
            </div>
            <p>Use the <strong>DNA Integrity Score</strong> to quickly assess account safety standing during audits.</p>
          </div>
        );
      case 'monitoring':
        return (
          <div className="doc-section animate-up">
            <h2>Real-time Monitoring</h2>
            <p>Admins have access to live telemetry describing the platform's health.</p>
            <ul>
              <li><strong>API Traffic:</strong> Distribution of requests between GitHub, LeetCode, and Codeforces.</li>
              <li><strong>Platform Latency:</strong> Automated connectivity checks performed every 60 minutes.</li>
              <li><strong>Growth Stats:</strong> 7-day registration velocity and active profile counters.</li>
            </ul>
          </div>
        );
      case 'resolutions':
        return (
          <div className="doc-section animate-up">
            <h2>Issue Resolutions</h2>
            <p>User reports (REP IDs) are centralized in the <strong>Report Dashboard</strong>.</p>
            <div className="workflow">
              <div className="step"><span>1</span> <strong>Review:</strong> Analyze UID history and SUID logs.</div>
              <div className="step"><span>2</span> <strong>Action:</strong> Suspend, Warn, or Resolve the ticket.</div>
              <div className="step"><span>3</span> <strong>Notify:</strong> Responses are sent via the user's secure reporting tab.</div>
            </div>
          </div>
        );
      case 'database':
        return (
          <div className="doc-section animate-up">
            <h2>Database Infrastructure</h2>
            <p>The Firebase Realtime Database is organized into logical nodes:</p>
            <pre className="code-block">
{`db/
 ├── users/         # Profile metadata & data links
 ├── admins/        # Staff roles & hierarchy
 ├── notifications/ # NID broadcast queue
 ├── changelog/     # Platform version history
 ├── reports/       # User ticket submissions (REP ID)
 ├── stats/         # Live API health & traffic logs
 ├── adminLogs/     # Audit trail for staff actions
 └── settings/      # Global maintenance flags`}
            </pre>
          </div>
        );
      case 'security-guidelines':
        return (
          <div className="doc-section animate-up">
            <h2>Section 10 — Security Guidelines</h2>
            <div className="guideline-list">
              <div className="guideline">
                <i className="fa-solid fa-check-double" style={{color: '#00ffae'}}></i>
                <span>Never expose internal IDs (UID, SID) in public UI elements.</span>
              </div>
              <div className="guideline">
                <i className="fa-solid fa-check-double" style={{color: '#00ffae'}}></i>
                <span>Superadmins should rotate platform API keys every 90 days.</span>
              </div>
              <div className="guideline">
                <i className="fa-solid fa-check-double" style={{color: '#00ffae'}}></i>
                <span>Monitor logs for sudden bursts of registration or login failures.</span>
              </div>
            </div>
          </div>
        );
      case 'ai-intel':
        return (
          <div className="doc-section animate-up">
            <h2>Administrative Intelligence</h2>
            <p>The **Origin Core** provides staff with basic platform health monitoring and data auditing capabilities.</p>
            
            <h3 style={{marginTop:'30px', color:'#fff'}}>Platform Health</h3>
            <p>Monitor the ratio of successful API syncs vs. failed requests. A healthy platform should maintain a success rate above 95%.</p>

            <h3 style={{marginTop:'30px', color:'#fff'}}>Data Auditing</h3>
            <p>Admins can manually audit profile data if a user reports a synchronization discrepancy. AI is used solely to flag potential profile ID collisions.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-docs-container">
      <div className="admin-page-header">
        <h1>Internal Staff Portal</h1>
        <p>Administrative Handbook & Security Guidelines</p>
      </div>

      <div className="docs-layout">
        <aside className="docs-sidebar glass-card">
          {sections.map(section => (
            <button 
              key={section.id} 
              className={`sidebar-link ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <i className={`fa-solid ${section.icon}`}></i>
              {section.title}
            </button>
          ))}
        </aside>

        <main className="docs-content glass-card">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default AdminDocumentation;
